import { RotationEngine, newEventRecord, type EventRecord, type ImportFiles, type PlatformAdapter, type PlatformContext, type Override, type ParticipantId, type RoomId, type BindingCandidate, type RebindResult, type LiveParticipant, type Discrepancy } from '@zrc/core';
import { ApiClient, fetchMe, type Me } from './api/client.js';
import { createAdapter, type AdapterKind } from './adapters/index.js';

export type Mode = 'backend' | 'local';

/**
 * Controller session: owns the adapter, the engine and persistence. Every
 * engine step is followed by save(); a reload restores the record from the
 * backend (or localStorage in local mode) and reconciles before enabling
 * controls (spec section 10).
 */
export class ControllerSession {
  adapter!: PlatformAdapter;
  adapterKind: AdapterKind = 'simulated';
  context: PlatformContext | null = null;
  engine: RotationEngine | null = null;
  mode: Mode = 'local';
  identity: Me = { kind: 'anonymous' };
  revision = 0;
  leaseToken: string | null = null;
  leaseHolder: string | null = null;
  lastReconcile: (RebindResult & { live: LiveParticipant[] }) | null = null;
  discrepancies: Discrepancy[] = [];
  error: string | null = null;
  notice: string | null = null;
  needsReconcileAfterReload = false;
  private readonly api = new ApiClient('/api', () => this.leaseToken);
  private renewTimer: number | null = null;
  private listeners = new Set<() => void>();
  private unsubscribe: (() => void)[] = [];
  readonly operator: string;

  constructor() {
    this.operator = 'operator';
    // Closing the tab or panel should not leave the event locked for two minutes.
    window.addEventListener('pagehide', () => {
      const id = this.engine?.record.eventId;
      if (id && this.leaseToken && this.mode === 'backend') {
        navigator.sendBeacon?.(`/api/events/${encodeURIComponent(id)}/lease/release`, new Blob([JSON.stringify({ token: this.leaseToken })], { type: 'application/json' }));
      }
    });
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
  private emit() {
    for (const l of this.listeners) l();
  }

  async init(): Promise<void> {
    const { kind, adapter } = await createAdapter();
    this.adapter = adapter;
    this.adapterKind = kind;
    this.context = await adapter.getContext().catch(() => null);
    this.identity = await fetchMe();
    if (this.identity.kind !== 'anonymous') {
      this.mode = 'backend';
    } else {
      this.mode = 'local';
      this.notice = 'Not signed in or backend unreachable: running in local mode, state kept in this browser only.';
    }
    this.unsubscribe.push(
      adapter.onRoomChange(() => {
        this.engine?.noteUncommandedChange();
        this.emit();
      }),
      adapter.onParticipantChange(() => this.emit()),
    );
    this.emit();
  }

  // ---- event lifecycle ----------------------------------------------------

  /** Admins see every event; an operator sees only the events a join code bound them to. */
  async listEvents() {
    if (this.mode === 'backend') return (this.identity.kind === 'operator' ? await this.api.operatorEvents() : await this.api.listEvents()).events;
    const raw = localStorage.getItem('zrc:events');
    const ids: string[] = raw ? JSON.parse(raw) : [];
    return ids
      .map((id) => localStorage.getItem(`zrc:event:${id}`))
      .filter((x): x is string => !!x)
      .map((x) => JSON.parse(x) as EventRecord)
      .map((r) => ({ eventId: r.eventId, title: r.title, state: r.state, updatedAt: '' }));
  }

  async createEvent(title: string, timezone: string): Promise<void> {
    if (this.mode === 'backend') {
      const stored = await this.api.createEvent(title, timezone);
      this.revision = stored.revision;
      await this.acquireLease(stored.eventId, false);
      this.attach(stored.record);
    } else {
      const record = newEventRecord(`local_${Date.now().toString(36)}`, title, timezone);
      const ids: string[] = JSON.parse(localStorage.getItem('zrc:events') ?? '[]');
      localStorage.setItem('zrc:events', JSON.stringify([...ids, record.eventId]));
      this.attach(record);
      await this.save();
    }
  }

  /**
   * Open an event. By default this also takes the control lease (one controller at a time). With
   * viewOnly the record is loaded for reading and no lease is taken, so looking never blocks the
   * person running the evening; take control later when something needs changing.
   */
  async loadEvent(eventId: string, takeLease = false, viewOnly = false): Promise<void> {
    if (this.mode === 'backend') {
      const stored = await this.api.getEvent(eventId);
      this.revision = stored.revision;
      if (viewOnly) {
        this.leaseToken = null;
        this.leaseHolder = stored.lease?.holder ?? null;
      } else {
        await this.acquireLease(eventId, takeLease);
      }
      this.attach(stored.record);
    } else {
      const raw = localStorage.getItem(`zrc:event:${eventId}`);
      if (!raw) throw new Error('Event not found in this browser');
      this.attach(JSON.parse(raw) as EventRecord);
    }
    // A reloaded controller must reconcile against the platform before any command (section 10).
    this.needsReconcileAfterReload = this.engine!.record.scheduleVersion !== null;
    this.emit();
  }

  private attach(record: EventRecord) {
    this.engine = new RotationEngine(record, this.adapter, { actor: this.operator });
    this.lastReconcile = null;
    this.discrepancies = [];
    this.error = null;
    this.emit();
  }

  private async acquireLease(eventId: string, take: boolean) {
    try {
      const l = await this.api.acquireLease(eventId, take, take ? null : this.leaseToken);
      this.leaseToken = l.token;
      this.leaseHolder = l.holder;
      // Renew well inside the 120 s TTL, naming our own token so the server knows it is us.
      if (this.renewTimer !== null) window.clearInterval(this.renewTimer);
      this.renewTimer = window.setInterval(() => void this.renew(eventId), 45_000);
    } catch (e) {
      this.leaseToken = null;
      throw e;
    }
  }

  /** Keep control. If someone else has taken it, say so instead of failing quietly at the next save. */
  private async renew(eventId: string): Promise<void> {
    if (!this.leaseToken) return;
    try {
      const l = await this.api.acquireLease(eventId, false, this.leaseToken);
      this.leaseHolder = l.holder;
    } catch (e) {
      if (e instanceof Error && /-> 409/.test(e.message)) {
        this.leaseToken = null;
        this.error = 'Another controller has taken control of this event. Take control back to continue.';
        this.emit();
      }
    }
  }

  /** Leave the current event: stop renewing and give up the control lease. */
  async closeEvent(): Promise<void> {
    const id = this.engine?.record.eventId;
    if (this.renewTimer !== null) window.clearInterval(this.renewTimer);
    this.renewTimer = null;
    if (id && this.mode === 'backend' && this.leaseToken) await this.api.releaseLease(id).catch(() => undefined);
    this.engine = null;
    this.leaseToken = null;
    this.leaseHolder = null;
    this.lastReconcile = null;
    this.discrepancies = [];
    this.error = null;
    this.emit();
  }

  async takeControl(): Promise<void> {
    if (!this.engine) return;
    await this.acquireLease(this.engine.record.eventId, true);
    this.emit();
  }

  get hasControl(): boolean {
    return this.mode === 'local' || this.leaseToken !== null;
  }

  async save(): Promise<void> {
    if (!this.engine) return;
    const record = this.engine.record;
    if (this.mode === 'backend') {
      let stored;
      try {
        stored = await this.api.saveEvent(record.eventId, this.revision, record);
      } catch (e) {
        // The lock lapses if the page was idle or asleep. If nobody else took it, quietly take it up again and retry once.
        if (!(e instanceof Error) || !/control lease/.test(e.message) || !this.leaseToken) throw e;
        await this.acquireLease(record.eventId, false);
        stored = await this.api.saveEvent(record.eventId, this.revision, record);
      }
      this.revision = stored.revision;
    } else {
      localStorage.setItem(`zrc:event:${record.eventId}`, JSON.stringify(record));
    }
    this.emit();
  }

  /** Run an engine action, persist, surface errors. */
  async run(label: string, fn: () => Promise<void> | void): Promise<void> {
    if (!this.engine) return;
    this.error = null;
    try {
      await fn();
    } catch (e) {
      this.error = `${label}: ${e instanceof Error ? e.message : String(e)}`;
    } finally {
      try {
        await this.save();
      } catch (e) {
        this.error = `${this.error ? this.error + '; ' : ''}save failed: ${e instanceof Error ? e.message : String(e)}`;
        this.emit();
      }
    }
  }

  // ---- actions -------------------------------------------------------------

  import(files: ImportFiles) {
    return this.run('import', () => {
      this.engine!.import(files);
    });
  }
  mapRooms(reuseByName: boolean) {
    return this.run('map rooms', async () => {
      await this.engine!.mapRooms({ reuseByName });
    });
  }
  reconcile() {
    return this.run('reconcile', async () => {
      this.lastReconcile = await this.engine!.reconcile();
      this.needsReconcileAfterReload = false;
    });
  }
  confirm(c: BindingCandidate) {
    return this.run('confirm match', () => {
      this.engine!.confirmCandidate(c);
      if (this.lastReconcile) this.lastReconcile = { ...this.lastReconcile, candidates: this.lastReconcile.candidates.filter((x) => x.participantId !== c.participantId && x.runtimeId !== c.runtimeId) };
    });
  }
  /** Confirm every unambiguous exact-name match in one go. Each is still the operator's decision, made once. */
  confirmExactMatches() {
    return this.run('confirm exact matches', () => {
      const cands = this.lastReconcile?.candidates ?? [];
      const clear = cands.filter((c) => c.reasons.includes('display name matches') && !c.reasons.some((r) => r.startsWith('several attendees') || r.startsWith('roster has more than one')));
      const usedP = new Set<string>();
      const usedR = new Set<string>();
      const chosen = clear.filter((c) => {
        if (usedP.has(c.participantId) || usedR.has(c.runtimeId)) return false;
        usedP.add(c.participantId);
        usedR.add(c.runtimeId);
        return true;
      });
      for (const c of chosen) this.engine!.confirmCandidate(c);
      if (this.lastReconcile) this.lastReconcile = { ...this.lastReconcile, candidates: this.lastReconcile.candidates.filter((x) => !usedP.has(x.participantId) && !usedR.has(x.runtimeId)) };
    });
  }
  markAbsent(pid: ParticipantId) {
    return this.run('mark absent', () => this.engine!.markAbsent(pid));
  }
  markReady() {
    return this.run('mark ready', () => this.engine!.markReady());
  }
  begin(txId: string, target: 'next' | 'end') {
    return this.run('begin transition', () => {
      this.engine!.beginTransition(txId, target);
    });
  }
  closeRooms(txId: string) {
    return this.run('close rooms', () => this.engine!.closeRooms(txId));
  }
  checkpoint(txId: string) {
    return this.run('checkpoint', async () => {
      this.lastReconcile = await this.engine!.checkpoint(txId);
    });
  }
  write(txId: string) {
    return this.run('write assignments', async () => {
      await this.engine!.writeAssignments(txId);
    });
  }
  open(txId: string) {
    return this.run('open rooms', () => this.engine!.openRooms(txId));
  }
  retry(txId: string) {
    return this.run('retry', async () => {
      await this.engine!.retryUnresolved(txId);
    });
  }
  acceptExceptions(txId: string) {
    return this.run('accept exceptions', () => this.engine!.acceptExceptions(txId));
  }
  endRotations(txId: string) {
    return this.run('end rotations', () => this.engine!.endRotations(txId));
  }
  addOverride(o: Omit<Override, 'createdBy' | 'createdAt'>) {
    return this.run('override', () => this.engine!.addOverride(o));
  }
  detectDiscrepancies() {
    return this.run('check rooms', async () => {
      this.discrepancies = await this.engine!.detectDiscrepancies();
    });
  }
  observe(pid: ParticipantId, roomId: RoomId, note?: string) {
    return this.run('record observation', () => this.engine!.recordManualObservation(pid, roomId, note));
  }
  /** Export is an admin function; an operator in Zoom does not get the link. */
  get canExport(): boolean {
    return this.identity.kind === 'admin' || this.identity.kind === 'dev';
  }
  exportUrl(): string | null {
    return this.canExport && this.mode === 'backend' && this.engine ? this.api.exportUrl(this.engine.record.eventId) : null;
  }
  /** Direct access to the API for the admin and operator consoles. */
  get client(): ApiClient {
    return this.api;
  }
}
